/**
 * The customer's money, answered before any clicking: what is still owed, what
 * has been invoiced and paid, how much of it is past due, and when they last
 * bought. The supplier side has had this strip since the supplier hub landed
 * (SupplierKpiHeader) — this is its AR twin.
 *
 * Money rule (ADR-0005): every figure is grouped BY CURRENCY and formatted with
 * formatMoney. Nothing is summed or converted across currencies, and every
 * comparison goes through Decimal — never a float.
 */
import type { Party, PortalStatement } from '@erp/shared';
import Decimal from 'decimal.js';
import { CalendarClock, HandCoins, Receipt, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { StatCard } from '../../components/ui/index.js';
import { formatDate } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

const GT_ZERO = '0.0001';

/** Past-due = everything outside the `current` bucket. */
export function overdueByCurrency(st: PortalStatement | null): Array<{ currencyCode: string; amount: string }> {
  return (st?.aging ?? [])
    .map((a) => ({
      currencyCode: a.currencyCode,
      amount: new Decimal(a.days_1_30).plus(a.days_31_60).plus(a.days_61_90).plus(a.days_90_plus).toFixed(4),
    }))
    .filter((a) => new Decimal(a.amount).gt(GT_ZERO));
}

export function CustomerKpiHeader({
  statement, loading, party,
}: {
  statement: PortalStatement | null;
  loading: boolean;
  party: Party;
}): JSX.Element {
  const { t } = useTranslation('parties');
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const money = (a: string, c: string): string => formatMoney(a, { locale, currency: c });

  const summary = statement?.summary ?? [];
  const owed = summary.filter((s) => new Decimal(s.balance).gt(GT_ZERO));
  // Every currency with a balance, a CREDIT too: a customer owing dinars while
  // holding a dollar credit showed only the dinars, as if the dollars were square.
  const balances = summary.filter((s) => new Decimal(s.balance).abs().gt(GT_ZERO));
  const overdue = overdueByCurrency(statement);
  // Invoices come back newest-first, so the head row is the most recent sale.
  // Credit notes are sales history too — they are not filtered out here.
  const lastSale = statement?.invoices[0]?.docDate ?? null;
  const openCount = (statement?.invoices ?? []).filter((i) => new Decimal(i.balance).gt(GT_ZERO)).length;

  /** A per-currency money column, or an em dash when there is nothing to show. */
  const column = (
    rows: Array<{ currencyCode: string; amount: string }>,
    empty?: JSX.Element,
  ): JSX.Element =>
    rows.length === 0
      ? (empty ?? <span className="text-fg-muted">—</span>)
      : (
        <span className="flex flex-col gap-0.5">
          {rows.map((r) => (
            <span key={r.currencyCode} className="font-mono tabular-nums">{money(r.amount, r.currencyCode)}</span>
          ))}
        </span>
      );

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      <StatCard
        label={t('customer.kpi.remaining', { defaultValue: 'Remaining' })}
        icon={Wallet}
        tone={owed.length ? 'warning' : 'success'}
        loading={loading}
        value={balances.length === 0
          ? <span className="text-success">{t('customer.kpi.settled', { defaultValue: 'Nothing owed' })}</span>
          : (
            <span className="flex flex-col gap-0.5">
              {balances.map((s) => {
                const credit = new Decimal(s.balance).lt(0);
                return (
                  <span key={s.currencyCode} className={`font-mono tabular-nums ${credit ? 'text-success' : ''}`}>
                    {money(s.balance, s.currencyCode)}
                    {credit && <span className="ms-1.5 font-sans text-xs font-semibold">({t('account.inCredit', { defaultValue: 'in credit' })})</span>}
                  </span>
                );
              })}
            </span>
          )}
        hint={balances.length > 1 ? t('supplier.kpi.perCurrency', { defaultValue: 'Per currency' }) : undefined}
      />
      <StatCard
        label={t('customer.kpi.overdue', { defaultValue: 'Overdue' })}
        icon={CalendarClock}
        tone={overdue.length ? 'danger' : 'neutral'}
        loading={loading}
        value={column(overdue, <span className="text-fg-muted">{t('customer.kpi.none', { defaultValue: 'None' })}</span>)}
        hint={overdue.length ? t('customer.kpi.pastDue', { defaultValue: 'Past its due date' }) : undefined}
      />
      <StatCard
        label={t('customer.kpi.invoiced', { defaultValue: 'Invoiced' })}
        icon={Receipt}
        tone="brand"
        loading={loading}
        value={column(summary.map((s) => ({ currencyCode: s.currencyCode, amount: s.invoiced })))}
        hint={statement ? t('customer.kpi.invoiceCount', { count: statement.invoices.length, defaultValue: '{{count}} invoices' }) : undefined}
      />
      <StatCard
        label={t('customer.kpi.paid', { defaultValue: 'Paid' })}
        icon={HandCoins}
        tone="success"
        loading={loading}
        value={column(summary.map((s) => ({ currencyCode: s.currencyCode, amount: s.paid })))}
        hint={openCount ? t('customer.kpi.openInvoices', { count: openCount, defaultValue: '{{count}} still open' }) : undefined}
      />
      <StatCard
        label={t('customer.kpi.lastSale', { defaultValue: 'Last purchase' })}
        icon={CalendarClock}
        tone="info"
        loading={loading}
        value={lastSale ? formatDate(lastSale, { locale }) : <span className="text-fg-muted">—</span>}
        hint={party.creditLimit && Number(party.creditLimit) > 0 && party.creditCurrencyCode
          ? t('customer.kpi.creditLeft', {
            amount: money(
              Decimal.max(
                new Decimal(party.creditLimit).minus(
                  summary.find((s) => s.currencyCode === party.creditCurrencyCode)?.balance ?? '0',
                ),
                0,
              ).toFixed(4),
              party.creditCurrencyCode,
            ),
            defaultValue: '{{amount}} credit left',
          })
          : undefined}
      />
    </div>
  );
}
